import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  api,
  markReconcileHandledByList,
  reconcileRequiredCount,
  type ApiError,
  type BatchWriteResult,
} from './client'

// M9 (Scheibe E): die Ergebnisform der Batch-Write-Routen. Definiert in
// `client.ts`, weil `campaigns.ts` dieselbe Form fuehrt — hier namensgleich
// re-exportiert, damit Konsumenten sie beim Hook finden.
export type { BatchWriteFailedItem, BatchWriteResult } from './client'

// --- Types ---

export interface Keyword {
  id: number
  adGroupId: number
  amazonKeywordId: string
  text: string
  matchType: 'broad' | 'phrase' | 'exact'
  bid: string | null
  state: 'enabled' | 'paused' | 'archived'
  promotionStage: 'auto' | 'broad' | 'phrase' | 'exact' | 'negative' | null
  createdAt: string
  updatedAt: string
  // v24: Joins aus der globalen Suche
  adGroupName?: string
  campaignId?: number
  campaignName?: string
  adType?: 'sp' | 'sb' | 'sd'
  // v24: Metriken
  impressions?: number
  clicks?: number
  spend?: number
  sales?: number
  orders?: number
  acos?: number | null
  roas?: number | null
  // v24: Duplikat-Detection
  isDuplicate?: boolean
}

export interface KeywordDetail extends Keyword {
  metrics7d: {
    impressions: number
    clicks: number
    spend: number
    sales: number
    orders: number
  }
  bidHistory: Array<{
    id: number
    oldBid: string | null
    newBid: string
    reason: string
    source: string
    createdAt: string
  }>
}

export interface KeywordFilters {
  search?: string
  matchType?: string
  state?: string
  promotionStage?: string
  campaignId?: number
  adGroupId?: number
  limit?: number
  offset?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  dateFrom?: string
  dateTo?: string
}

// --- Hooks ---

export function useKeywordsByAdGroup(adGroupId: number | null, filters: KeywordFilters = {}) {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.matchType) params.set('matchType', filters.matchType)
  if (filters.state) params.set('state', filters.state)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  const qs = params.toString()

  return useQuery({
    queryKey: ['keywords', 'adGroup', adGroupId, filters],
    queryFn: () => api.get<{ keywords: Keyword[]; total: number }>(`/ad-groups/${adGroupId}/keywords/${qs ? `?${qs}` : ''}`),
    enabled: adGroupId !== null,
  })
}

export function useKeywordsGlobal(filters: KeywordFilters = {}) {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.matchType) params.set('matchType', filters.matchType)
  if (filters.state) params.set('state', filters.state)
  if (filters.promotionStage) params.set('promotionStage', filters.promotionStage)
  if (filters.campaignId) params.set('campaignId', String(filters.campaignId))
  if (filters.adGroupId) params.set('adGroupId', String(filters.adGroupId))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortDir) params.set('sortDir', filters.sortDir)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  const qs = params.toString()

  return useQuery({
    queryKey: ['keywords', 'global', filters],
    queryFn: () => api.get<{ keywords: Keyword[]; total: number; dateFrom?: string; dateTo?: string }>(`/keywords/${qs ? `?${qs}` : ''}`),
  })
}

export function useKeyword(id: number | null) {
  return useQuery({
    queryKey: ['keyword', id],
    queryFn: () => api.get<{ keyword: KeywordDetail }>(`/keywords/${id}/`),
    enabled: id !== null,
  })
}

export function useUpdateKeywordBid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, bid, reason }: { id: number; bid: string; reason?: string }) =>
      api.put(`/keywords/${id}/bid/`, { bid, reason: reason ?? 'Manuelle Bid-Aenderung' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keywords'] })
      qc.invalidateQueries({ queryKey: ['keyword'] })
    },
  })
}

export function useBatchKeywordBids() {
  const qc = useQueryClient()
  return useMutation<BatchWriteResult, ApiError, { updates: Array<{ id: number; bid: string }>; reason: string }>({
    mutationFn: ({ updates, reason }) =>
      api.post<BatchWriteResult>('/keywords/batch/bid/', { updates, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keywords'] })
    },
    onError: (err) => {
      // Teilerfolge sind bereits bei Amazon — die Liste muss neu laden wie im Erfolgsfall.
      if (reconcileRequiredCount(err) > 0) qc.invalidateQueries({ queryKey: ['keywords'] })
    },
    // BEWUSST OHNE `markReconcileHandledByList` (anders als bei den beiden Hooks
    // mit Listen-Banner): dieser Hook hat derzeit keinen Konsumenten und damit
    // keine Oberflaeche, die den 502 darstellen wuerde. Den Toast hier zu
    // unterdruecken hiesse, den Hinweis fuer einen kuenftigen Aufrufer lautlos zu
    // verlieren. Wer den Hook verdrahtet, zeigt den Fall an — und markiert ihn dann.
  })
}

export function useBatchKeywordState() {
  const qc = useQueryClient()
  // Fix-Runde 1 (F1): Der Hook wird IN `KeywordList` aufgerufen und teilt damit deren
  // Lebenszyklus. Nur solange die Liste lebt, kann sie den Hinweis auch zeigen —
  // sonst darf der globale Toast NICHT unterdrueckt werden (siehe unten).
  // `isMounted.current = true` steht bewusst im Effekt-RUMPF, nicht nur `false` im
  // Cleanup: Refs ueberleben in React StrictMode das simulierte Unmount/Remount des
  // Dev-Builds (main.tsx rendert unter StrictMode). Ein reiner Cleanup-Setter haette
  // den Hook danach dauerhaft fuer unmountet gehalten.
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  return useMutation<BatchWriteResult, ApiError, { ids: number[]; state: string }>({
    mutationFn: async ({ ids, state }) => {
      try {
        return await api.post<BatchWriteResult>('/keywords/batch/state/', { ids, state })
      } catch (err) {
        // Muss HIER stehen und nicht in `onError`: der globale MutationCache-Handler
        // (main.tsx) laeuft VOR jedem `onError`. `KeywordList` zeigt den Fall selbst an —
        // ABER nur, wenn es sie noch gibt. Ist der Operator waehrend des Writes
        // weggeblaettert, bleibt `toasted` unberuehrt und er bekommt den Toast. Ein
        // verschluckter Hinweis waere schlimmer als ein doppelter.
        if (isMounted.current) markReconcileHandledByList(err)
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keywords'] })
    },
    onError: (err) => {
      // Ein 502 aus `ackOrReconcile` ist ein TEILergebnis: `result.success` Eintraege
      // sind geschrieben. Ohne Invalidierung zeigte die Liste weiter den alten Stand.
      if (reconcileRequiredCount(err) > 0) qc.invalidateQueries({ queryKey: ['keywords'] })
    },
  })
}
